import type { FC, ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useAuthStatus, useUserInitializationContext } from '@/features/user'
import { useSessionStatusesQuery, useHostStatusesQuery } from '@/features/events/hydration/api'
import { STATUS_SESSION_CHANGED_EVENT, STATUS_HOST_CHANGED_EVENT } from '@/infra/sse/constants'
import type {
  SessionStatus,
  HostStatus,
  SessionStatusChangedPayload,
  HostStatusChangedPayload,
} from '@/infra/sse/status'

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

interface StatusContextValue {
  readonly getSessionStatus: (sessionId: string) => SessionStatus | null
  readonly getHostStatus: (hostname: string) => HostStatus | null
  readonly isLoading: boolean
}

interface StatusProviderProps {
  readonly children: ReactNode
}

const StatusContext = createContext<StatusContextValue | null>(null)

export const StatusProvider: FC<StatusProviderProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuthStatus()
  const { userInitialized } = useUserInitializationContext()

  const enabled = isAuthenticated && userInitialized && !!user?.access_token

  const sessionStatusesQuery = useSessionStatusesQuery({ enabled })
  const hostStatusesQuery = useHostStatusesQuery({ enabled })

  // Maps stored in refs for O(1) lookups, version counter triggers re-renders
  const sessionMapRef = useRef(new Map<string, SessionStatus>())
  const hostMapRef = useRef(new Map<string, HostStatus>())
  const [version, setVersion] = useState(0)

  // Hydrate session statuses from REST response
  useEffect(() => {
    if (!sessionStatusesQuery.data) return

    for (const entry of sessionStatusesQuery.data) {
      if (entry.status) {
        sessionMapRef.current.set(entry.claudeSessionId, entry.status)
      }
    }
    setVersion((v) => v + 1)
  }, [sessionStatusesQuery.data])

  // Hydrate host statuses from REST response
  useEffect(() => {
    if (!hostStatusesQuery.data) return

    for (const entry of hostStatusesQuery.data) {
      if (entry.status) {
        hostMapRef.current.set(entry.hostName, entry.status)
      }
    }
    setVersion((v) => v + 1)
  }, [hostStatusesQuery.data])

  // Subscribe to real-time session status events via globalThis event bus
  useEffect(() => {
    const handler = (e: Event) => {
      const payload = (e as CustomEvent<SessionStatusChangedPayload>).detail
      sessionMapRef.current.set(payload.claudeSessionId, payload.status)
      setVersion((v) => v + 1)
    }
    globalThis.addEventListener(STATUS_SESSION_CHANGED_EVENT, handler)
    return () => globalThis.removeEventListener(STATUS_SESSION_CHANGED_EVENT, handler)
  }, [])

  // Subscribe to real-time host status events via globalThis event bus
  useEffect(() => {
    const handler = (e: Event) => {
      const payload = (e as CustomEvent<HostStatusChangedPayload>).detail
      hostMapRef.current.set(payload.hostName, payload.status)
      setVersion((v) => v + 1)
    }
    globalThis.addEventListener(STATUS_HOST_CHANGED_EVENT, handler)
    return () => globalThis.removeEventListener(STATUS_HOST_CHANGED_EVENT, handler)
  }, [])

  // Callbacks depend on version to ensure consumers re-render with fresh data
  const getSessionStatus = useCallback(
    (sessionId: string): SessionStatus | null => sessionMapRef.current.get(sessionId) ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [version],
  )

  const getHostStatus = useCallback(
    (hostname: string): HostStatus | null => hostMapRef.current.get(hostname) ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [version],
  )

  const isLoading = sessionStatusesQuery.isLoading || hostStatusesQuery.isLoading

  const value = useMemo<StatusContextValue>(
    () => ({
      getSessionStatus,
      getHostStatus,
      isLoading,
    }),
    [getSessionStatus, getHostStatus, isLoading],
  )

  return <StatusContext.Provider value={value}>{children}</StatusContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useStatusContext(): StatusContextValue {
  const context = useContext(StatusContext)
  if (!context) {
    throw new Error('useStatusContext must be used within StatusProvider')
  }
  return context
}
